import { z } from 'zod'
import type { Capability } from './capabilities'

// Merging a person with no BRE account into another person (ADR 0055).
// Staff do it from the person page; nothing merges on a name, email or
// phone match (rule 7). Two people who both have BRE accounts are never
// merged here: that is BRE Auth's job, and Hub follows Auth (ADR 0009).

export const PERSON_MERGE_GROUPS = [
  'contact',
  'links',
  'organizations',
  'notes',
  'work',
  'cases',
  'documents',
  'forms',
  'workforce',
  'shifts',
  'vendors',
  'operations',
  'incidents',
  'lost_found',
  'calls',
  'governance',
  'merged_people',
] as const
export type PersonMergeGroup = (typeof PERSON_MERGE_GROUPS)[number]

export const PERSON_MERGE_GROUP_LABELS: Record<PersonMergeGroup, string> = {
  contact: 'Contact details',
  links: 'Records in BRE apps',
  organizations: 'Organizations',
  notes: 'Notes',
  work: 'Tasks, projects, requirements and workflows',
  cases: 'Support cases',
  documents: 'Documents',
  forms: 'Form submissions',
  workforce: 'Staff and volunteer records',
  shifts: 'Shifts',
  vendors: 'Vendors, funders, grants and purchasing',
  operations: 'Assets, work orders, inspections and reminders',
  incidents: 'Incident reports',
  lost_found: 'Lost and found',
  calls: 'Calls and phone lines',
  governance: 'Board service and governance records',
  merged_people: 'People merged into this one earlier',
}

// One kind of reference, e.g. "Board terms" or "Support cases they asked for".
//   moving  rows re-pointed at the person kept
//   left    rows that stay on the merged-away record because the person
//           kept already has the same thing (an exact duplicate)
export type PersonMergeLine = { key: string; group: PersonMergeGroup; label: string; moving: number; left: number }

// Something that can't simply move: the person kept already has a board
// term on those dates, a vote on the same motion, a link to the same kind of
// app record. Every conflict needs a choice before the merge goes ahead.
//   leave  the rows stay on the merged-away record (they don't move)
//   move   move them anyway (only where both can stand, e.g. two app links)
export type PersonMergeResolution = 'leave' | 'move'
export type PersonMergeConflict = {
  key: string
  group: PersonMergeGroup
  label: string
  explanation: string
  count: number
  choices: PersonMergeResolution[]
  chosen: PersonMergeResolution | null
}

// What doesn't move, and why. readThrough rows are shown on the person kept
// through the merge pointer (they are append-only evidence and never
// rewritten).
export type PersonMergeStay = { key: string; label: string; count: number; why: string }

// The few references worth seeing one by one before merging.
export type PersonMergeDetail = { kind: 'contact' | 'link' | 'membership'; text: string; outcome: 'moves' | 'duplicate' | 'conflict' }

export type PersonMergeSide = {
  personId: string
  displayName: string
  status: 'active' | 'inactive'
  provenance: string
  primaryEmail: string | null
  // A BRE account (an identity link, active or not, or a BRE Auth record link).
  breAccount: boolean
}

export type PersonMergeRefusal = { code: string; message: string }

export type PersonMergePreview = {
  merged: PersonMergeSide
  target: PersonMergeSide
  // null when the merge may go ahead once every conflict has a choice.
  refusal: PersonMergeRefusal | null
  lines: PersonMergeLine[]
  conflicts: PersonMergeConflict[]
  stays: PersonMergeStay[]
  readThrough: PersonMergeStay[]
  details: PersonMergeDetail[]
  // Capabilities moving these records needs beyond people.manage, and which
  // of them the person asking lacks.
  needs: Capability[]
  missing: Capability[]
  totals: { moving: number; left: number }
}

export const PersonMergePreviewBody = z.object({
  targetPersonId: z.string().min(1).max(100),
  resolutions: z.record(z.string().max(100), z.enum(['leave', 'move'])).optional(),
})
export type PersonMergePreviewBody = z.infer<typeof PersonMergePreviewBody>

export const PersonMergeBody = PersonMergePreviewBody.extend({
  reason: z.string().trim().min(3).max(1000),
})
export type PersonMergeBody = z.infer<typeof PersonMergeBody>

export const PersonMergeUndoBody = z.object({ reason: z.string().trim().min(3).max(1000) })
export type PersonMergeUndoBody = z.infer<typeof PersonMergeUndoBody>

export type PersonMergeRecordLine = { group: PersonMergeGroup; label: string; moved: number; left: number; restored: number; kept: number }

export type PersonMergeRecord = {
  id: string
  merged: { personId: string; displayName: string }
  target: { personId: string; displayName: string }
  reason: string
  mergedBy: { personId: string; displayName: string } | null
  mergedAt: string
  status: 'applied' | 'undone'
  undoneAt: string | null
  undoneBy: { personId: string; displayName: string } | null
  undoReason: string | null
  lines: PersonMergeRecordLine[]
  totals: { moved: number; left: number; restored: number; kept: number }
  // While applied: how many moved rows still point at the person kept (an
  // undo puts them back) and how many changed since (an undo leaves them).
  undo: { restorable: number; changedSince: number; refusal: PersonMergeRefusal | null } | null
}
